'use client'

/**
 * Purpose: Survey camera consent, capture, face-analysis, and retention-confirmation
 *   flow before the questionnaire.
 * Inputs: locale, onAnalyzed, onUseQuestionnaire.
 * Outputs: Invokes onAnalyzed with FaceAnalysisResponse + consent timestamp once the
 *   user has acknowledged what happened to their photo.
 */

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { CameraCapture } from '@components/captures/Camera'
import { SurveyCameraAnalyzing } from '@components/captures/SurveyCameraCapture/SurveyCameraAnalyzing'
import { SurveyCameraConfirmation } from '@components/captures/SurveyCameraCapture/SurveyCameraConfirmation'
import { SurveyCameraConsent } from '@components/captures/SurveyCameraCapture/SurveyCameraConsent'
import { SurveyCameraOutcome } from '@components/captures/SurveyCameraCapture/SurveyCameraOutcome'
import { useSurveyFaceAnalysis } from '@components/captures/SurveyCameraCapture/hooks/use-survey-face-analysis'
import type { FaceAnalysisResponse } from '@lib/models/face-api'
import { isFaceImageBase64WithinLimit } from '@lib/image-limits'
import './SurveyCameraCapture.css'

// Every screen the flow can show; outcome stages share one component.
type Stage =
  | 'consent'
  | 'choose'
  | 'analyzing'
  | 'confirmation'
  | 'noFace'
  | 'multipleFaces'
  | 'imageBlurry'
  | 'imageUnderexposed'
  | 'unsafeContent'
  | 'rateLimited'
  | 'visionUnavailable'
  | 'error'

// Orchestrates consent → capture → analysis → questionnaire fallback.
export function SurveyCameraCapture({
  locale,
  onAnalyzed,
  onUseQuestionnaire,
}: {
  locale: string
  onAnalyzed: (result: FaceAnalysisResponse, consentTimestamp: string) => void
  onUseQuestionnaire: () => void
}) {
  const t = useTranslations('survey.photo')
  const [stage, setStage] = useState<Stage>('consent')
  const [imageConsent, setImageConsent] = useState(false)
  // Optional research opt-in — unticked by default and never required to scan.
  const [researchConsent, setResearchConsent] = useState(false)
  const [consentTimestamp, setConsentTimestamp] = useState('')
  // Held between the confirmation screen and onAnalyzed so the survey only
  // advances once the user has seen what happened to their photo.
  const [analysis, setAnalysis] = useState<FaceAnalysisResponse | null>(null)
  // Inline camera errors clear when the user retries capture.
  const [cameraError, setCameraError] = useState<string | null>(null)
  // Set when the server reports the vision service has no credentials configured.
  const [notConfigured, setNotConfigured] = useState(false)
  // ISO timestamp from a 429 — shown on the rateLimited outcome screen.
  const [retryAfter, setRetryAfter] = useState<string | null>(null)

  // Shared hook posts captured base64 to /api/face-analysis.
  const runAnalysis = useSurveyFaceAnalysis({
    researchConsent,
    // A response with no retention record (upload skipped or failed) has nothing
    // truthful to confirm, so skip straight to the survey rather than promise a
    // deletion the system never scheduled.
    onSuccess: (result) => {
      if (!result.deletionDate && !result.imageDeleted) {
        onAnalyzed(result, consentTimestamp)
        return
      }
      setAnalysis(result)
      setStage('confirmation')
    },
    setStage,
    setCameraError,
    setNotConfigured,
    setRetryAfter,
  })

  // Only the biometric consent gates capture; the research box may stay unchecked.
  function recordConsent() {
    if (!imageConsent) return
    setConsentTimestamp(new Date().toISOString())
    setStage('choose')
  }

  // GDPR-style consent screen before any camera or upload UI.
  if (stage === 'consent') {
    return (
      <SurveyCameraConsent
        locale={locale}
        imageConsent={imageConsent}
        researchConsent={researchConsent}
        onImageConsentChange={setImageConsent}
        onResearchConsentChange={setResearchConsent}
        onContinue={recordConsent}
        onUseQuestionnaire={onUseQuestionnaire}
      />
    )
  }

  // Capture or upload triggers face-analysis via shared CameraCapture primitive.
  if (stage === 'choose') {
    return (
      <div id="survey-camera-capture">
        <h1 className="survey-camera-capture-choose-title">{t('intro.title')}</h1>
        <p className="survey-camera-capture-description">{t('chooseDescription')}</p>
        {/* Immediate capture mode — analysis runs on shutter, not held preview. */}
        <CameraCapture
          id="survey-camera-capture-controls"
          facingMode="user"
          error={cameraError}
          labels={{
            capture: t('capture'),
            upload: t('upload'),
            cancel: t('cancel'),
            takePhoto: t('takePhoto'),
            sizeHint: t('uploadHint'),
          }}
          // Size-check the image before it is sent for analysis.
          onCapture={(base64) => void runAnalysis(base64)}
          validateBase64={isFaceImageBase64WithinLimit}
          onValidationError={() => setCameraError(t('imageTooLarge'))}
          onStreamError={(reason) => {
            // Distinguish permission denial from generic camera failures.
            setCameraError(reason === 'permission_denied' ? t('cameraDenied') : t('cameraError'))
          }}
          onFrameCaptureFailed={() => setCameraError(t('cameraError'))}
          onError={() => {
            // Unrecoverable file read errors skip analysis and show the error stage.
            setStage('error')
          }}
          showPreviewWhenControlled={false}
        />
        {/* Plain centered text link (no outline/pill) so it reads as a tertiary action. */}
        <button
          id="survey-camera-choose-questionnaire"
          type="button"
          onClick={onUseQuestionnaire}
          className="survey-camera-capture-text-action"
        >
          {t('useQuestionnaire')}
        </button>
      </div>
    )
  }

  // Full-screen spinner while GCV + Claude run on the server.
  if (stage === 'analyzing') {
    return <SurveyCameraAnalyzing />
  }

  // Retention outcome, stated before the survey moves on. Every field comes from
  // the API response — the deletion date is the one the server stored.
  if (stage === 'confirmation' && analysis) {
    return (
      <SurveyCameraConfirmation
        locale={locale}
        deletionDate={analysis.deletionDate ?? null}
        imageDeleted={analysis.imageDeleted ?? false}
        researchConsent={analysis.researchConsent ?? false}
        onContinue={() => onAnalyzed(analysis, consentTimestamp)}
      />
    )
  }

  // Collapse no-face / multi-face / quality / safe-content / rate-limit / generic errors
  // into one outcome component — each stage maps to its own translation key prefix.
  const outcomeStages = [
    'noFace',
    'multipleFaces',
    'imageBlurry',
    'imageUnderexposed',
    'unsafeContent',
    'rateLimited',
    'visionUnavailable',
  ] as const
  const outcomeStage = (outcomeStages as readonly string[]).includes(stage)
    ? (stage as (typeof outcomeStages)[number])
    : 'error'

  return (
    <SurveyCameraOutcome
      stage={outcomeStage}
      notConfigured={notConfigured}
      locale={locale}
      retryAfter={retryAfter}
      // Retry clears every error flag and returns to the capture chooser.
      onTryAgain={() => {
        setNotConfigured(false)
        setCameraError(null)
        setRetryAfter(null)
        setStage('choose')
      }}
      onUseQuestionnaire={onUseQuestionnaire}
    />
  )
}
